import { useRef } from "react";
import { FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtDate, fmtNum } from "@/lib/format";
import { elementToPdf } from "@/lib/pdf";
import { BigButton } from "@/components/ui-kit";
import { ReportHeader, ReportFooter, RTd } from "./ReportFrame";

const V = ["temp", "bp", "pulse", "spo2", "weight", "height", "glucose"];

function Visit({ e, t, lang }) {
  const extra = Object.entries(e.extra || {}).filter(([k, v]) => k !== "teeth" && v);
  return (
    <div className="pdf-block mb-3" data-testid="visit-block">
      <div className="font-bold text-[12px] mb-1 flex justify-between"><span dir="ltr">{fmtDate(e.date, lang)}</span><span>{e.doctor_name}{e.specialty && ` · ${t(`sp_${e.specialty}`)}`}</span></div>
      <table className="w-full text-[12px]">
        <tbody>
          {V.some((k) => e.vitals?.[k]) && <tr><RTd bold nowrap>{t("vitals")}</RTd><RTd>{V.filter((k) => e.vitals?.[k]).map((k) => <span key={k} className="me-4"><b>{t(`vital_${k}`)}:</b> <span dir="ltr">{e.vitals[k]}</span></span>)}</RTd></tr>}
          {e.symptoms && <tr><RTd bold nowrap>{t("symptoms")}</RTd><RTd>{e.symptoms}</RTd></tr>}
          {e.diagnosis && <tr><RTd bold nowrap>{t("diagnosis")}</RTd><RTd>{e.diagnosis}</RTd></tr>}
          {extra.length > 0 && <tr><RTd bold nowrap>{t("details")}</RTd><RTd>{extra.map(([k, v]) => `${t(`tp_${k}`)}: ${v}`).join(" · ")}</RTd></tr>}
          {e.prescription?.length > 0 && <tr><RTd bold nowrap>{t("prescription")}</RTd><RTd><div>{e.prescription.map((r, i) => <div key={r.id || i}><span dir="ltr">{i + 1}.</span> {r.drug} — <span dir="ltr">{r.dose}</span> {r.duration}</div>)}</div></RTd></tr>}
          {e.notes && <tr><RTd bold nowrap>{t("hr_notes")}</RTd><RTd>{e.notes}</RTd></tr>}
          {e.follow_up && <tr><RTd bold nowrap>{t("follow_up")}</RTd><RTd dir="ltr">{e.follow_up}</RTd></tr>}
          {e.services?.length > 0 && <tr><RTd bold nowrap>{t("services")}</RTd><RTd>{e.services.map((s) => s.name).join(", ")} · {fmtNum(e.total || 0)}</RTd></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function VisitReportDialog({ patient, encounters, title, onClose }) {
  const { t, lang } = useApp();
  const ref = useRef(null);
  const last = encounters[0];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dlg-full p-0 bg-[#F0F0F0]" data-testid="visit-report-dialog">
        <div className="max-w-3xl mx-auto w-full p-4 space-y-3">
          <DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
          <div ref={ref} className="rounded border border-[#CCCCCC] bg-white p-6 text-[12px] text-[#222] pdf-page" data-testid="visit-report">
            <ReportHeader title={title} patient={patient} date={encounters.length === 1 ? last.date : null} />
            {patient.allergies && <p className="text-xs text-[#C7353B] font-bold mb-2">{t("allergies")}: {patient.allergies}</p>}
            {patient.chronic && <p className="text-xs mb-2"><b>{t("chronic")}:</b> {patient.chronic}</p>}
            {encounters.map((e) => <Visit key={e.id} e={e} t={t} lang={lang} />)}
            <ReportFooter doctorName={last?.doctor_name} doctorId={last?.doctor_id} />
          </div>
          <div className="flex gap-2">
            <BigButton variant="soft" onClick={onClose} data-testid="visit-report-close">{t("back")}</BigButton>
            <BigButton onClick={() => elementToPdf(ref.current, `${title}-${patient.file_no || patient.name}.pdf`)} className="flex-1" data-testid="visit-report-pdf-btn"><FileDown className="w-5 h-5" />PDF</BigButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
